"use client"

import { Button } from "@/components/ui/button"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { cn } from "@/lib/utils"
import { format } from "date-fns"
import { enAU } from "date-fns/locale"
import { CalendarIcon, ChevronLeft, ChevronRight } from "lucide-react"
import { useMemo, useState, type FC } from "react"

export type MonthPickerProps = Readonly<{
  date: Date
  setDate: (date: Date) => void
  className?: string
  placeholder?: string
}>

const MonthPicker: FC<MonthPickerProps> = ({ date, setDate, className }) => {
  const [open, setOpen] = useState(false)
  const [viewYear, setViewYear] = useState(date.getFullYear())
  const [viewMode, setViewMode] = useState<"months" | "years">("months")

  const months = useMemo(() => {
    const result = []
    for (let i = 0; i < 12; i++) {
      const monthDate = new Date(viewYear, i, 1)
      result.push({
        date: monthDate,
        name: format(monthDate, "MMMM", { locale: enAU }),
        isCurrentMonth: new Date().getMonth() === i && new Date().getFullYear() === viewYear,
        isSelected: date.getMonth() === i && date.getFullYear() === viewYear,
      })
    }
    return result
  }, [viewYear, date])

  const years = useMemo(() => {
    const result = []
    const startYear = viewYear - 5

    for (let i = 0; i < 12; i++) {
      const year = startYear + i
      result.push({
        year,
        isCurrentYear: new Date().getFullYear() === year,
        isSelected: date.getFullYear() === year,
      })
    }
    return result
  }, [viewYear, date])

  const handlePrevYear = () => {
    setViewYear((prev) => prev - 1)
  }

  const handleNextYear = () => {
    setViewYear((prev) => prev + 1)
  }

  const handlePrevYearRange = () => {
    setViewYear((prev) => prev - 12)
  }

  const handleNextYearRange = () => {
    setViewYear((prev) => prev + 12)
  }

  const handleMonthSelect = (monthDate: Date) => {
    setDate(new Date(monthDate.getFullYear(), monthDate.getMonth(), 1))
    setOpen(false)
  }

  const handleYearSelect = (year: number) => {
    setViewYear(year)
    setViewMode("months")
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          className={cn("w-full justify-start text-left font-normal", className)}
          onClick={() => setOpen(true)}
        >
          <CalendarIcon className="mr-2 size-4" />
          {format(date, "MMMM yyyy", { locale: enAU })}
          <span className="text-muted-foreground ml-auto text-xs">Click to select</span>
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-64 p-0" align="start">
        {viewMode === "months" && (
          <div className="p-3">
            <div className="mb-4 flex items-center justify-between">
              <Button variant="ghost" size="icon" onClick={handlePrevYear}>
                <ChevronLeft className="size-4" />
              </Button>
              <Button
                variant="ghost"
                className="text-sm font-medium"
                onClick={() => setViewMode("years")}
              >
                {viewYear}
              </Button>
              <Button variant="ghost" size="icon" onClick={handleNextYear}>
                <ChevronRight className="size-4" />
              </Button>
            </div>

            <div className="grid grid-cols-3 gap-2">
              {months.map((month) => (
                <Button
                  key={month.date.toISOString()}
                  variant={month.isSelected ? "default" : "outline"}
                  className={cn(
                    "h-10 text-xs",
                    month.isCurrentMonth && !month.isSelected && "bg-muted",
                  )}
                  onClick={() => handleMonthSelect(month.date)}
                >
                  {month.name}
                </Button>
              ))}
            </div>
          </div>
        )}

        {viewMode === "years" && (
          <div className="p-3">
            <div className="mb-4 flex items-center justify-between">
              <Button variant="ghost" size="icon" onClick={handlePrevYearRange}>
                <ChevronLeft className="size-4" />
              </Button>
              <span className="text-sm font-medium">
                {years[0]?.year} - {years[years.length - 1]?.year}
              </span>
              <Button variant="ghost" size="icon" onClick={handleNextYearRange}>
                <ChevronRight className="size-4" />
              </Button>
            </div>

            <div className="grid grid-cols-3 gap-2">
              {years.map((year) => (
                <Button
                  key={year.year}
                  variant={year.isSelected ? "default" : "outline"}
                  className={cn(
                    "h-10 text-xs",
                    year.isCurrentYear && !year.isSelected && "bg-muted",
                  )}
                  onClick={() => handleYearSelect(year.year)}
                >
                  {year.year}
                </Button>
              ))}
            </div>
          </div>
        )}
      </PopoverContent>
    </Popover>
  )
}

export default MonthPicker
